import React from 'react';
import Form from '#/common/components/Form';
import fields, { ShowroomDataType } from './fields';
import type { FormAction } from '#/common/components/Form/FormProps';

export interface ShowroomFormProps {
  value?: ShowroomDataType;
}

export const ShowroomForm: React.FC<ShowroomFormProps> = ({ value }) => {
  const defaultData: ShowroomDataType = {
    __typename: 'ShowroomDataType',
    id: 'test-123',
    tag: '',
    autocompleteField: 'Apple',
    calendarField: new Date(),
    checkBoxField: true,
    colorBoxField: '#ff5722',
    currencyField: 0.0,
    dateBoxField: new Date(),
    dateTimeBoxField: new Date(),
    timeBoxField: new Date(),
    dropDownBoxField: 'opt1',
    htmlEditorField: '<p>Rich text content</p>',
    imageField: '',
    lookupField: '1',
    numberBoxField: 42,
    radioGroupField: 'Option A',
    rangeSliderField: [20, 80],
    relatedItemsField: '',
    selectBoxField: 'Medium',
    sliderField: 50,
    switchField: false,
    tagBoxField: ['React', 'TypeScript'],
    textAreaField: 'This is a multi-line\ntext area example',
    textBoxField: 'Sample text',
  };

  // Get current data for form - use passed value or default
  const currentData = value || defaultData;

  // Define action buttons for the form demonstrating different button types:
  // Primary (Save), Secondary (Cancel), Active (Export), Inactive/Disabled (Disabled Action)
  const actions: FormAction<ShowroomDataType>[] = [
    {
      label: 'Save', // PRIMARY ACTION - prominent save button
      type: 'submit', // Primary button type
      onClick: async ({ record, form }) => {
        console.log('Saving record:', record);
        // Simulate async save operation
        await new Promise((resolve) => setTimeout(resolve, 500));
        alert('Record saved successfully!');
      },
    },
    {
      label: 'Cancel', // SECONDARY ACTION - supporting cancel button
      type: 'normal', // Secondary button type
      onClick: ({ record, form }) => {
        console.log('Cancel clicked, current record:', record);
        if (confirm('Are you sure you want to cancel? Any unsaved changes will be lost.')) {
          // Reset form or navigate away
          form?.resetValues();
          alert('Form cancelled and reset.');
        }
      },
    },
    {
      label: 'Export', // ACTIVE - clickable and enabled export functionality
      type: 'normal', // Active button type
      onClick: ({ record, form }) => {
        console.log('Exporting record:', record);
        // Create a simple JSON export
        const dataStr = JSON.stringify(record, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `showroom-form-${record?.id || 'data'}.json`;
        link.click();
        URL.revokeObjectURL(url);
      },
    },
    {
      label: 'Disabled Action', // INACTIVE/DISABLED - always disabled
      type: 'normal',
      disabled: true,
      onClick: ({ record, form }) => {
        // This won't be called since the button is disabled
        console.log('This should not execute');
      },
    },
  ];

  return <Form record={currentData as ShowroomDataType} fields={fields} actions={actions} />;
};

export default React.memo(ShowroomForm);
